import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import ReactApexChart from 'react-apexcharts';

const PlantHireRateForm = ({
  show,
  editingPlantRate,
  openPlantRateInputGroupKeys,
  setOpenPlantRateInputGroupKeys,
  plantRateInputGroups,
  renderEditField,
  plantRateForm,
  plantRateTableRows,
  plantRateCategories,
  planningOccurrencesResult,
  onOpenPlantRateCategories,
  onOpenProjectSettings,
  onClose,
  onSave,
  projectSettings,
}) => {

  const [activeReviewTab, setActiveReviewTab] = useState('costBreakdown');
  const plantRateTableRef = useRef(null);
  const [reviewPanelHeight, setReviewPanelHeight] = useState(320);


  const formatCurrency = (value) => Number(value || 0).toFixed(2);
  const formatWholeNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(Number(value || 0)));
  const utilisationPeriodSize = Math.max(1, Number(plantRateForm?.utilisation || 0));
  const assetEndLife = Number(plantRateForm?.endUsage || 0);
  const startUsage = Number(plantRateForm?.startUsage || 0);

  const costBreakdownRows = useMemo(
    () => (plantRateTableRows?.rows || []).filter((row) => Number(row?.value || 0) > 0),
    [plantRateTableRows?.rows]
  );

  const donutSeries = useMemo(
    () => costBreakdownRows.map((row) => Number(row?.value || 0)),
    [costBreakdownRows]
  );

  const donutOptions = useMemo(
    () => ({
      labels: costBreakdownRows.map((row) => row?.label || 'Unknown'),
      legend: {
        position: 'bottom',
        fontSize: '12px',
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (value) => Number(value || 0).toFixed(2),
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                value: {
                  formatter: (value) => Number(value || 0).toFixed(2),
                },
                label: 'Total',
                formatter: () => Number(plantRateTableRows?.total || 0).toFixed(2),
              },
            },
          },
        },
      },
    }),
    [costBreakdownRows, plantRateTableRows?.total]
  );

  const cashflowData = useMemo(() => {
    const bucketForUsage = (usage) => Math.ceil(Number(usage || 0) / utilisationPeriodSize) * utilisationPeriodSize;
    const firstVisibleColumn = startUsage > 0 ? bucketForUsage(startUsage) : utilisationPeriodSize;
    const lastVisibleColumn = Math.max(firstVisibleColumn, bucketForUsage(assetEndLife), utilisationPeriodSize);
    const periods = [];
     for (let usage = firstVisibleColumn; usage <= lastVisibleColumn; usage += utilisationPeriodSize) periods.push(usage);
    const periodSet = new Set(periods);

    const getCategoryRate = (codes) => {
      const codeSet = new Set(codes.map((code) => String(code).toUpperCase()));
      return (plantRateCategories || [])
        .filter((category) => Number(category?.include) === 1 && codeSet.has(String(category?.code || '').trim().toUpperCase()))
        .reduce((sum, category) => sum + Number(plantRateTableRows?.rows?.find((row) => String(row?.code || '').trim().toUpperCase() === String(category?.code || '').trim().toUpperCase())?.value || 0), 0);
    };

    const costDefinitions = [
      { label: 'Ownership', rate: getCategoryRate(['DP', 'IN', 'IS']) },
      { label: 'GET', rate: getCategoryRate(['GT']) },
      { label: 'Tyres', rate: getCategoryRate(['TR']) },
      { label: 'Overheads', rate: getCategoryRate(['OH']) },
      { label: 'Operator', rate: getCategoryRate(['OP']) },
      { label: 'Fluids', rate: getCategoryRate(['FL']) },
      { label: 'Fuel', rate: getCategoryRate(['FU']) },
    ].filter((row) => row.rate > 0);

    const partsByPeriod = {};
    const labourByPeriod = {};
    (planningOccurrencesResult?.assets || []).forEach((asset) => {
      (asset?.costItems || []).forEach((costItem) => {
        (costItem?.forecastOccurrences || []).forEach((occurrence) => {
          const bucket = bucketForUsage(occurrence?.usage);
          if (!periodSet.has(bucket)) return;
          partsByPeriod[bucket] = Number(partsByPeriod[bucket] || 0) + Number(costItem?.partsCost || 0);
          labourByPeriod[bucket] = Number(labourByPeriod[bucket] || 0) + Number(costItem?.labourCost || 0);
        });
      });
    });

    const revenueByPeriod = {};
    const totalCostsByPeriod = {};
    const underOverByPeriod = {};
    periods.forEach((period) => {
      const revenue = Number(plantRateTableRows?.total || 0) * utilisationPeriodSize;
      const standardCosts = costDefinitions.reduce((sum, row) => sum + (Number(row.rate || 0) * utilisationPeriodSize), 0);
      const totalCosts = standardCosts + Number(partsByPeriod[period] || 0) + Number(labourByPeriod[period] || 0);
      revenueByPeriod[period] = revenue;
      totalCostsByPeriod[period] = totalCosts;
      underOverByPeriod[period] = revenue - totalCosts;
    });

    return { periods, costDefinitions, partsByPeriod, labourByPeriod, revenueByPeriod, totalCostsByPeriod, underOverByPeriod };
  }, [assetEndLife, plantRateCategories, plantRateForm?.utilisation, planningOccurrencesResult, plantRateTableRows?.rows, plantRateTableRows?.total, startUsage, utilisationPeriodSize]);


  const summaryRates = useMemo(() => {
    const usageRange = Math.max(0, assetEndLife - startUsage);
    const ownershipRate = (plantRateTableRows?.rows || [])
      .filter((r) => ['DP', 'IN', 'IS'].includes(String(r?.code || '').toUpperCase()))
      .reduce((sum, r) => sum + Number(r?.value || 0), 0);
    const periodTotals = cashflowData.periods.reduce((acc, period) => {
      acc.parts += Number(cashflowData.partsByPeriod?.[period] || 0);
      acc.labour += Number(cashflowData.labourByPeriod?.[period] || 0);
      return acc;
    }, { parts: 0, labour: 0 });
    const partsRate = usageRange > 0 ? periodTotals.parts / usageRange : 0;
    const labourRate = usageRange > 0 ? periodTotals.labour / usageRange : 0;
    const otherCostRate = (plantRateTableRows?.rows || [])
      .filter((r) => {
        const code = String(r?.code || '').toUpperCase();
        const label = String(r?.label || '').trim().toUpperCase();
        return !['DP', 'IN', 'IS', 'PA', 'LA', 'MG', 'OH'].includes(code)
          && !['MARGIN', 'OVERHEADS'].includes(label)
          && Number(r?.value || 0) > 0;
      })
      .reduce((sum, r) => sum + Number(r?.value || 0), 0);
    const totalCosts = ownershipRate + otherCostRate + partsRate + labourRate;
    return { ownershipRate, partsRate, labourRate, otherCostRate, totalCosts, overUnder: Number(plantRateTableRows?.total || 0) - totalCosts };
  }, [assetEndLife, cashflowData.labourByPeriod, cashflowData.partsByPeriod, cashflowData.periods, plantRateTableRows?.rows, plantRateTableRows?.total, startUsage]);

  if (!show) return null;

  return (
    <div className='modal show d-block'>
      <div className='modal-fullscreen m-0'>
        <div className='bg-white rounded-0'>
          <div className='modal-header'>
            <h5 className='modal-title'>{`${editingPlantRate?.name} / ${editingPlantRate?.group?.manufacturer?.manufacturer} ${editingPlantRate?.group?.groupName} ${editingPlantRate?.group?.class?.className}` || 'Edit Plant Rate'}</h5>
            <button className='btn-close' onClick={onClose} />
          </div>
          <div className='modal-body'>
            <div className='row g-3'>
              <div className='col-lg-3 border-end'>
                <div className='d-flex justify-content-between align-items-start mb-16'>
                  <h6 className='text-xl mt-12 mb-0 ms-8'>Inputs</h6>
                  <button
                    className='btn btn-outline-primary text-xl p-2 d-inline-flex align-items-center justify-content-center flex-shrink-0'
                    style={{ height: '40px', width: '40px' }}
                    onClick={onOpenProjectSettings}
                  >
                    <Icon icon='mdi:settings-outline' />
                  </button>
                </div>
                <div style={{ height: "calc(100vh - 220px)", overflow: "auto" }}>
                  <table className='table mb-0'>
                    <tbody>
                      {plantRateInputGroups.map((group, groupIndex) => {
                        const isMaintenanceGroup = group.key === 'maintenance' || group.label === 'Maintenance';
                        return (
                          <React.Fragment key={group.key ?? `input-group-${groupIndex}`}>
                            <tr className='table-light'>
                              <td className='bg-neutral-50'>
                                <button
                                  type='button'
                                  className='btn btn-link d-flex align-items-center gap-2 px-8 py-8 text-decoration-none text-black'
                                  onClick={() => setOpenPlantRateInputGroupKeys((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                                >
                                  <Icon icon={openPlantRateInputGroupKeys[group.key] ? 'mdi:chevron-down' : 'mdi:chevron-right'} />
                                  <span className='fw-semibold'>{group.label}</span>
                                  {group.suffix && <span className='text-muted text-sm text-uppercase'>{group.suffix}</span>}
                                  <span className='text-muted text-sm'>({group.fields.length})</span>
                                </button>
                              </td>
                            </tr>
                            {openPlantRateInputGroupKeys[group.key] && (
                              <tr>
                                <td>
                                  {group.fields.map(([label, field, options], fieldIndex) => (
                                    <React.Fragment key={`${group.key ?? `input-group-${groupIndex}`}-${field ?? `field-${fieldIndex}`}`}>
                                      {renderEditField(label, field, options || {})}
                                    </React.Fragment>
                                  ))}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className='col-lg-9'>
                <div className='row g-3 align-items-stretch'>
                  <div className='col-lg-5 d-flex flex-column'>
                    <div className='d-flex justify-content-between align-items-start mb-16'>
                      <h6 className='text-xl mt-12 mb-0 ms-8'>Plant Rate</h6>
                      <button
                        className='btn btn-outline-primary text-xl p-2 d-inline-flex align-items-center justify-content-center flex-shrink-0'
                        style={{ height: '40px', width: '40px' }}
                        onClick={onOpenPlantRateCategories}
                      >
                        <Icon icon='mdi:settings-outline' />
                      </button>
                    </div>
                    <div ref={plantRateTableRef} className='h-100'><table className='table bordered-table p-0 mb-0'>
                      <thead>
                        <tr>
                          <th style={{padding: "0"}}>Category</th>
                          <th className="tight-table-padding">Value</th>
                          <th className="py-2">Expense Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(plantRateTableRows?.rows || []).map((row, rowIndex) => {
                          const rowKey = row.code ?? row.id ?? `${row.label ?? 'row'}-${row.expense ?? 'expense'}-${rowIndex}`;
                          return (
                            <tr key={rowKey}>
                              <td>{row.label}</td>
                              <td className="text-end">{Number(row.value || 0).toFixed(2)}</td>
                              <td className="pt-10 text-muted text-uppercase text-sm">{row.expense}</td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td className="fw-bold">Total</td>
                          <td className="text-end fw-bold">{Number(plantRateTableRows?.total || 0).toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table></div>
                  </div>
                  <div className='col-lg-7 d-flex flex-column'>
                    <h6 className='text-xl ms-6 mt-12' style={{ marginBottom: '22px' }}>Review</h6>
                    <ul className='nav nav-tabs mb-2'>
                      <li className='nav-item'><button type='button' className={`nav-link ${activeReviewTab === 'costBreakdown' ? 'active' : ''}`} onClick={() => setActiveReviewTab('costBreakdown')}>Cost Break down</button></li>
                      <li className='nav-item'><button type='button' className={`nav-link ${activeReviewTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveReviewTab('summary')}>Summary</button></li>
                      <li className='nav-item'><button type='button' className={`nav-link ${activeReviewTab === 'assumptions' ? 'active' : ''}`} onClick={() => setActiveReviewTab('assumptions')}>Assumptions</button></li>
                    </ul>
                    <div className='border rounded-2 bg-light p-3 h-100' style={{ minHeight: `${reviewPanelHeight}px` }}>
                      {activeReviewTab === 'costBreakdown' && (donutSeries.length > 0 ? <ReactApexChart options={donutOptions} series={donutSeries} type='donut' height={Math.max(280, reviewPanelHeight - 48)} /> : <div className='d-flex align-items-center justify-content-center h-100'><span className='text-muted'>No cost element values available.</span></div>)}
                      {activeReviewTab === 'summary' && (
                        <table className='table table-sm mb-0'>
                          <tbody>
                            <tr className='table bordered-table'><td className='fw-semibold'>Revenue</td><td></td></tr>
                            <tr><td>Total Revenue</td><td className='text-end'>{formatCurrency(plantRateTableRows?.total || 0)}</td></tr>
                            <tr className='table-light'><td className='fw-semibold'>Costs</td><td></td></tr>
                            <tr><td>Ownership</td><td className='text-end'>{formatCurrency(summaryRates.ownershipRate)}</td></tr>
                            {(plantRateTableRows?.rows || []).filter((r) => {
                              const code = String(r?.code || '').toUpperCase();
                              const label = String(r?.label || '').trim().toUpperCase();
                              return !['DP', 'IN', 'IS', 'MG', 'OH'].includes(code)
                                && !['PARTS', 'LABOUR', 'MARGIN', 'OVERHEADS'].includes(label)
                                && Number(r?.value || 0) > 0;
                            }).map((row) => <tr key={`summary-${row.code || row.label}`}><td>{row.label}</td><td className='text-end'>{formatCurrency(row.value)}</td></tr>)}
                            <tr><td>Parts</td><td className='text-end'>{formatCurrency(summaryRates.partsRate)}</td></tr>
                            <tr><td>Labour</td><td className='text-end'>{formatCurrency(summaryRates.labourRate)}</td></tr>
                            <tr className='fw-bold'><td>Total Costs</td><td className='text-end'>{formatCurrency(summaryRates.totalCosts)}</td></tr>
                            <tr className='fw-bold'><td>Over / Under Recovery</td><td className='text-end'>{formatCurrency(summaryRates.overUnder)}</td></tr>
                          </tbody>
                        </table>
                      )}
                      {activeReviewTab === 'assumptions' && (
                        <table className='table table-sm mb-0'>
                          <tbody>
                            <tr><td>currency</td><td className='text-end'>{projectSettings?.currency?.symbol || projectSettings?.currency?.name || projectSettings?.currencyId || ''}</td></tr>
                            <tr><td>Interest Rate</td><td className='text-end'>{formatCurrency(projectSettings?.interestRate || 0)}</td></tr>
                            <tr><td>Insurance Rate</td><td className='text-end'>{formatCurrency(projectSettings?.insuranceRate || 0)}</td></tr>
                            <tr><td>technicianCost</td><td className='text-end'>{formatCurrency(projectSettings?.technicianRate || projectSettings?.technicianCost || 0)}</td></tr>
                            <tr><td>operatorCost</td><td className='text-end'>{formatCurrency(projectSettings?.operatorRate || projectSettings?.operatorCost || 0)}</td></tr>
                            <tr><td>fuelPrice</td><td className='text-end'>{formatCurrency(projectSettings?.fuelPrice || 0)}</td></tr>
                            <tr><td>AnnualUsage</td><td className='text-end'>{formatWholeNumber(plantRateForm?.utilisation || 0)}</td></tr>
                            <tr><td>Start Usage</td><td className='text-end'>{formatWholeNumber(plantRateForm?.startUsage || 0)}</td></tr>
                            <tr><td>End Usage</td><td className='text-end'>{formatWholeNumber(plantRateForm?.endUsage || 0)}</td></tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                  <div className='col-12'>
                    <h6 className='text-xl mb-12 ms-8'>Cash Flow</h6>
                    <div className='table-responsive'>
                      <table className='table bordered-table mb-0'>
                        <thead>
                          <tr>
                            <th>Item</th>
                            {cashflowData.periods.map((period) => <th key={period} className='text-end'>{period}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className='table-light'><td colSpan={cashflowData.periods.length + 1} className='fw-semibold'>Revenue</td></tr>
                          <tr className='text-primary fw-bold'>
                            <td>Revenue</td>
                            {cashflowData.periods.map((period) => <td key={`rev-${period}`} className='text-end'>{formatWholeNumber(cashflowData.revenueByPeriod[period])}</td>)}
                          </tr>
                          <tr className='table-light'><td colSpan={cashflowData.periods.length + 1} className='fw-semibold'>Costs</td></tr>
                          {cashflowData.costDefinitions.map((row) => (
                            <tr key={row.label} className='text-black'>
                              <td>{row.label}</td>
                              {cashflowData.periods.map((period) => <td key={`${row.label}-${period}`} className='text-end'>{formatWholeNumber(row.rate * utilisationPeriodSize)}</td>)}
                            </tr>
                          ))}
                          <tr className='text-black'>
                            <td>Parts</td>
                            {cashflowData.periods.map((period) => <td key={`parts-${period}`} className='text-end'>{formatWholeNumber(cashflowData.partsByPeriod[period])}</td>)}
                          </tr>
                          <tr className='text-black'>
                            <td>Labour</td>
                            {cashflowData.periods.map((period) => <td key={`labour-${period}`} className='text-end'>{formatWholeNumber(cashflowData.labourByPeriod[period])}</td>)}
                          </tr>
                          <tr className='fw-bold'>
                            <td>Total Costs</td>
                            {cashflowData.periods.map((period) => <td key={`total-cost-${period}`} className='text-end'>{formatWholeNumber(cashflowData.totalCostsByPeriod[period])}</td>)}
                          </tr>
                          <tr className='table-light'><td colSpan={cashflowData.periods.length + 1} className='fw-semibold'>Under / Over Recovery</td></tr>
                          <tr className='fst-italic'>
                            <td>Under / Over Recovery</td>
                            {cashflowData.periods.map((period) => {
                              const value = Number(cashflowData.underOverByPeriod[period] || 0);
                              return <td key={`uor-${period}`} className={`text-end ${value < 0 ? 'text-danger' : ''}`}>{formatWholeNumber(value)}</td>;
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <button className='btn btn-secondary' onClick={onClose}>Close</button>
            {/*<button className='btn btn-primary' onClick={onSave}>Save</button>*/}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantHireRateForm;
